<template>
    <div class="c-date-picker">
        <VueDatePicker
            v-model="range"
            range
            focus-start-date
            arrow-navigation
            :multi-calendars="2"
            :time-config="timeConfig"
            :min-date="minDate"
            :formats="formatConfig"
            model-type="format"
            :aria-labels="ariaLabels"
            :input-attrs="{ clearable: true, id: visibleInputId, hideInputIcon: true }"
            :ui="{ input: 'c-date-picker__input', menu: 'c-date-picker__menu' }"
            :action-row="{ selectBtnLabel: 'Select', cancelBtnLabel: 'Cancel' }"
            placeholder="Choose dates"
            @update:model-value="onUpdate"
            @cleared="onClear"
        />
        <input
            type="hidden"
            :id="hiddenInputId"
            name="date"
            :value="hiddenValue"
        >
    </div>
</template>

<script>

    import { defineComponent, ref, computed, nextTick as returnFocus } from 'vue'
    import { VueDatePicker } from '@vuepic/vue-datepicker'
    import '@vuepic/vue-datepicker/dist/main.css'

    export default defineComponent({
        name: 'DatePickerFilter',

        components: {
            VueDatePicker,
        },

        // Pass values from the data-hidden-input-id and data-initial-value attributes 
        props: {
            hiddenInputId: {
                type: String,
                default: 'c-date-select',
            },
            initialValue: {
                type: String,
                default: '',
            },
        },

        setup(props) {
            // Apply the initial date value to the range if it's valid, otherwise return null
            const parseInitialValue = (value) => {
                if (!value) {
                    return null
                }

                const parts = value.split('|')

                if (parts.length === 2 && parts[0] && parts[1]) {
                    return parts
                }

                return null
            }

            // The range of dates the user has picked
            const range = ref(parseInitialValue(props.initialValue))

            // Disable the time picker
            const timeConfig = {
                enableTimePicker: false,
            }

            // Set minDate to today so the user can't pick a date in the past
            const minDate = new Date()

            // Set the date format for the picker elements
            const formatConfig = { input: 'dd-MM-yyyy', preview: 'dd-MM-yyyy' }

            // Apply custom aria label to the date picker input
            const ariaLabels = { input: 'Filter events by date' }

            // Create the ID for the visible text input.
            // This should match the ID referenced by the label in the filters template
            const visibleInputId = `${props.hiddenInputId}-input`

            // Create the date range value to be submitted with the form
            const hiddenValue = computed(() => {
                if (Array.isArray(range.value) && range.value.length === 2 && range.value[0] && range.value[1]) {
                    return range.value.join('|')
                }

                return ''
            })

            // Update the range whenever the user picks a new date
            const onUpdate = (value) => {
                range.value = value
            }

            // Reset values when clear button is presed
            const onClear = () => {
                range.value = null

                // When the clear button is pressed, return focus to the input
                returnFocus(() => {
                    document.getElementById(visibleInputId)?.focus()
                })
            }

            return {
                range,
                minDate,
                timeConfig,
                formatConfig,
                ariaLabels,
                visibleInputId,
                hiddenValue,
                onUpdate,
                onClear,
            }
        },
    })
</script>
